import { create } from 'zustand'

import type { ColorSchemeId, ThemeMode } from '@/stores/settings'

type AppearanceContext = {
	userId: string
	workspaceId: string
}

type StoredAppearance = {
	themeMode: ThemeMode
	colorSchemeId: ColorSchemeId
}

// Default appearance when nothing is stored yet: dark mode + the Ghibli scheme.
const DEFAULT_APPEARANCE: StoredAppearance = {
	themeMode: 'dark',
	colorSchemeId: 'ghibli',
}

function getStorageKey(context: AppearanceContext) {
	return `dam-appearance:${context.userId}:${context.workspaceId}`
}

function readFromStorage(context: AppearanceContext): StoredAppearance {
	try {
		const raw = localStorage.getItem(getStorageKey(context))
		if (!raw) return DEFAULT_APPEARANCE
		const parsed = JSON.parse(raw) as Partial<StoredAppearance> | null
		if (!parsed) return DEFAULT_APPEARANCE
		return {
			themeMode: (parsed.themeMode ?? DEFAULT_APPEARANCE.themeMode) as ThemeMode,
			colorSchemeId: (parsed.colorSchemeId ?? DEFAULT_APPEARANCE.colorSchemeId) as ColorSchemeId,
		}
	} catch {
		return DEFAULT_APPEARANCE
	}
}

function writeToStorage(context: AppearanceContext, value: StoredAppearance) {
	try {
		localStorage.setItem(getStorageKey(context), JSON.stringify(value))
	} catch {
		// Ignore storage quota / privacy mode failures
	}
}

function getPersistedState<T>(key: string): T | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = localStorage.getItem(key)
		if (!raw) return null
		const parsed = JSON.parse(raw) as { state?: T } | null
		return parsed?.state ?? null
	} catch {
		return null
	}
}

function getBootstrapContext(): AppearanceContext | null {
	const auth = getPersistedState<{ user: { id: string } | null }>('dam-auth')
	const workspace = getPersistedState<{ currentWorkspaceId: string | null }>('dam-workspace')
	const userId = auth?.user?.id
	const workspaceId = workspace?.currentWorkspaceId
	if (!userId || !workspaceId) return null
	return { userId, workspaceId }
}

const BOOTSTRAP_CONTEXT = getBootstrapContext()
const BOOTSTRAP_APPEARANCE = BOOTSTRAP_CONTEXT
	? readFromStorage(BOOTSTRAP_CONTEXT)
	: DEFAULT_APPEARANCE

export interface AppearanceState {
	context: AppearanceContext | null
	hasLoaded: boolean

	// Settings values
	themeMode: ThemeMode
	colorSchemeId: ColorSchemeId

	// Context management
	setContext: (context: AppearanceContext | null) => void

	// Setters
	setThemeMode: (mode: ThemeMode) => void
	setColorSchemeId: (id: ColorSchemeId) => void

	// Computed
	getCurrentTheme: () => 'light' | 'dark'

	// Reset to defaults
	reset: () => void
}

export const useAppearanceStore = create<AppearanceState>()((set, get) => ({
	context: BOOTSTRAP_CONTEXT,
	hasLoaded: !!BOOTSTRAP_CONTEXT,
	...BOOTSTRAP_APPEARANCE,

	setContext: (context) => {
		const prev = get().context
		if (prev?.userId === context?.userId && prev?.workspaceId === context?.workspaceId) return

		if (!context) {
			set({ context: null, hasLoaded: false, ...DEFAULT_APPEARANCE })
			return
		}

		const stored = readFromStorage(context)
		set({ context, hasLoaded: true, ...stored })
	},

	setThemeMode: (themeMode) =>
		set((state) => {
			const next: StoredAppearance = { themeMode, colorSchemeId: state.colorSchemeId }
			if (state.context) writeToStorage(state.context, next)
			return { themeMode }
		}),

	setColorSchemeId: (colorSchemeId) =>
		set((state) => {
			const next: StoredAppearance = { themeMode: state.themeMode, colorSchemeId }
			if (state.context) writeToStorage(state.context, next)
			return { colorSchemeId }
		}),

	getCurrentTheme: () => {
		const { themeMode } = get()
		if (typeof window === 'undefined') return 'light'
		if (themeMode === 'system') {
			return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
		}
		return themeMode
	},

	reset: () =>
		set((state) => {
			if (state.context) writeToStorage(state.context, DEFAULT_APPEARANCE)
			return { ...DEFAULT_APPEARANCE }
		}),
}))
